<script setup lang="ts">
import { computed, useSlots } from 'vue';

import AppSvgIcon from '../appSvgIcon/AppSvgIcon.vue';

import type {
  AppCheckboxGap,
  AppCheckboxSize,
  AppCheckboxTheme,
} from './AppCheckbox.model';

interface AppCheckboxProps {
  checked: boolean;
  theme: AppCheckboxTheme;
  size: AppCheckboxSize;
  gap?: AppCheckboxGap;
  isDisabled?: boolean;
}
const props = withDefaults(defineProps<AppCheckboxProps>(), {
  isDisabled: false,
});

interface AppCheckboxEmits {
  (event: 'change', value: boolean): void;
}
const emit = defineEmits<AppCheckboxEmits>();

const slots = useSlots();
const hasTextSlot = computed(() => !!slots['default']);
const gap = computed(() => `var(--${props.gap ?? 'spacing-8'})`);

function handleChange(): void {
  emit('change', !props.checked);
}
</script>

<template>
  <label
    class="app-checkbox"
    :class="[props.size, props.theme, { disabled: props.isDisabled }]"
  >
    <div
      class="checkbox"
      :class="{ checked: props.checked, disabled: props.isDisabled }"
    >
      <input
        type="checkbox"
        :checked="props.checked"
        class="input"
        @change="handleChange"
      />
      <AppSvgIcon
        v-if="checked"
        name="check"
        :color="props.isDisabled ? 'color-neutral-30' : 'color-white'"
        size="18"
      />
    </div>
    <div v-if="hasTextSlot" class="label" :class="{ checked: props.checked }">
      <slot />
    </div>
  </label>
</template>

<style scoped lang="scss">
@use '../../styles/media' as *;
@use '../../styles/fonts' as *;
.app-checkbox {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: v-bind(gap);
  width: fit-content;
  &:not(.disabled) {
    @include on-hover {
      cursor: pointer;
    }
  }

  .checkbox {
    border-radius: var(--border-radius-8);
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;

    .input {
      display: none;
    }
  }

  .label {
    user-select: none;
  }

  // PROPS SETTINGS

  // // SIZE
  &.extra-small {
    --extra-small-checkbox-size: var(--spacing-24);
    .checkbox {
      height: var(--extra-small-checkbox-size);
      width: var(--extra-small-checkbox-size);
      min-height: var(--extra-small-checkbox-size);
      min-width: var(--extra-small-checkbox-size);
    }
    .label {
      @include font-label-s-light;
    }
  }

  &.small {
    --small-checkbox-size: 26px;
    .checkbox {
      height: var(--small-checkbox-size);
      width: var(--small-checkbox-size);
      min-height: var(--small-checkbox-size);
      min-width: var(--small-checkbox-size);
    }
    .label {
      @include font-label-m-light;
    }
  }

  &.large {
    --large-checkbox-size: 30px;
    .checkbox {
      height: var(--large-checkbox-size);
      width: var(--large-checkbox-size);
      min-height: var(--large-checkbox-size);
      min-width: var(--large-checkbox-size);
    }
    .label {
      @include font-label-m-light;
    }
  }

  // // THEME && STATE
  &.secondary {
    .checkbox {
      background-color: var(--color-white);
      border: 2px solid var(--color-neutral-50);
      &.checked {
        border: none;
        background-color: var(--color-neutral-70);
      }

      &.disabled {
        background-color: var(--color-neutral-30);
        border: 2px solid var(--color-neutral-30);

        &.checked {
          outline: none;
        }
      }
    }
    @include on-hover {
      .checkbox:not(.checked):not(.disabled) {
        background-color: var(--color-neutral-30);
        outline: 12px solid var(--color-neutral-30);
        border: 2px solid var(--color-neutral-70);
      }
    }
    .label {
      color: var(--color-neutral-70);
      &.checked {
        color: var(--color-neutral-70);
      }
    }
    .checkbox:focus-visible {
      background-color: var(--color-neutral-30);
      outline: 12px solid var(--color-neutral-30);
      border: 2px solid var(--color-neutral-70);
    }

    &.disabled {
      .label,
      .label.checked {
        color: var(--color-neutral-40);
      }
    }
  }
  &.grey {
    .checkbox {
      background-color: var(--color-neutral-30);
      &.checked {
        background-color: var(--color-brand-violet);
      }

      &.disabled {
        background-color: var(--color-neutral-10);
        outline: 1px solid var(--color-neutral-30);
        &.checked {
          outline: none;
        }
      }
    }
    @include on-hover {
      .checkbox:not(.checked):not(.disabled) {
        background-color: var(--color-neutral-30);
      }
    }
    .label {
      color: var(--color-neutral-70);
    }
  }

  &.grey-dark {
    .checkbox {
      background-color: var(--color-neutral-30);
      &.checked {
        background-color: var(--color-brand-violet);
      }

      &.disabled {
        background-color: var(--color-neutral-10);
        outline: 1px solid var(--color-neutral-30);
        &.checked {
          outline: none;
        }
      }
    }
    @include on-hover {
      .checkbox:not(.checked):not(.disabled) {
        background-color: var(--color-neutral-30);
      }
    }
    .label {
      color: var(--color-neutral-70);
      &.checked {
        color: var(--color-neutral-70);
      }
    }
  }

  &.white {
    .checkbox {
      background-color: var(--color-white);
      &.checked {
        background-color: var(--color-neutral-70);
      }

      &.disabled {
        background-color: var(--color-white);
        outline: 1px solid var(--color-neutral-30);
        &.checked {
          outline: none;
        }
      }
    }
    @include on-hover {
      .checkbox:not(.checked):not(.disabled) {
        background-color: var(--color-neutral-30);
      }
    }
  }

  .label {
    color: var(--color-neutral-70);
  }

  &.disabled {
    .label,
    .label.checked {
      color: var(--color-secondary-90);
    }
  }
}
</style>
